/*
 * Campaign module client. Operator-only: external (cPanel) mailboxes and all their mail,
 * subscriber lists, and campaigns. Same transport as lib/api.ts (same-origin, cookie session).
 */
import { get, post, patch, del, humanise } from './api'

export interface CmMailbox {
  id: string; email: string; domain: string; display_name?: string | null
  smtp_host: string; smtp_port: number; imap_host: string; imap_port: number
  created_on_cpanel: boolean; sending_enabled: boolean; sync_enabled: boolean
  sent_folder?: string | null; last_sync_at?: string | null; last_sync_error?: string | null
  auth_failed: boolean; incoming: number; unread: number; outgoing: number; created_at: string
}

export interface CmUsage { host: string; last_hour: number; last_day: number; hourly_cap: number; daily_cap: number }

export interface CmSetup {
  cpanel_configured: boolean; allowed_domains: string[]
  default_smtp_host?: string | null; default_smtp_port: number
  default_imap_host?: string | null; default_imap_port: number
  default_quota_mb: number; hourly_cap: number; daily_cap: number
  usage: CmUsage[]
}

export interface CmMailItem {
  id: string; mailbox?: string | null; folder: string; direction: 'in' | 'out'
  from?: string | null; from_name?: string | null; to?: string | null; subject?: string | null
  snippet?: string | null; is_read: boolean; is_bounce: boolean; is_campaign: boolean
  has_attachments: boolean; date: string
}

export interface CmMailDetail {
  id: string; mailbox?: string | null; mailbox_id?: string | null; folder: string; direction: 'in' | 'out'
  message_id?: string | null; from?: string | null; from_name?: string | null; to?: string | null; cc?: string | null
  subject?: string | null; text?: string | null; html?: string | null; attachments?: string | null
  is_bounce: boolean; campaign_id?: string | null; campaign_name?: string | null; date: string
}

export interface CmList {
  id: string; name: string; description?: string | null; double_opt_in: boolean; redirect_url?: string | null
  default_mailbox_id?: string | null; default_mailbox?: string | null; default_from_email?: string | null; subscribe_url: string
  subscribed: number; pending: number; unsubscribed: number; bounced: number; created_at: string
}

export interface CmMember {
  contact_id: string; email: string; name?: string | null; status: string; status_reason?: string | null
  consent_source: string; consent_ip?: string | null; consent_at: string
  confirmed_at?: string | null; unsubscribed_at?: string | null
}

export interface CmCampaign {
  id: string; name: string; status: string
  list_id?: string | null; list_name?: string | null; mailbox_id?: string | null; mailbox?: string | null
  from_email?: string | null
  from_name?: string | null; reply_to?: string | null; subject: string
  html?: string | null; text?: string | null; footer_address?: string | null
  scheduled_at?: string | null; started_at?: string | null; finished_at?: string | null
  total: number; sent: number; failed: number; bounced: number; unsubscribed: number
  created_at: string; updated_at: string
}

export interface CmDelivery {
  handed_over: number; in_queue: number; delivered: number; deferred: number; bounced: number
  complained: number; suppressed: number; opened: number; clicked: number
}

export interface CmSpamCheck {
  content_score: number; verdict: 'good' | 'review' | 'likely_spam'; filter_score: number
  findings: { name: string; score: number; description?: string | null }[]
}

export interface CmCampaignDetail {
  campaign: CmCampaign; queued: number; eligible_recipients: number; eta_minutes: number
  delivery?: CmDelivery | null
  host_usage?: CmUsage | null
}

export interface CmSend {
  email: string; status: string; attempts: number; error?: string | null
  queued_at: string; next_attempt_at?: string | null; sent_at?: string | null
}

export type CampaignFields = Partial<Record<
  'name' | 'list_id' | 'mailbox_id' | 'from_email' | 'from_name' | 'reply_to' | 'subject' | 'html' | 'text' | 'footer_address', string>>

const qs = (o: Record<string, string | number | boolean | undefined | null>) => {
  const p = new URLSearchParams()
  for (const [k, v] of Object.entries(o)) if (v !== undefined && v !== null && v !== '') p.set(k, String(v))
  const s = p.toString()
  return s ? `?${s}` : ''
}

export const cm = {
  setup: () => get<CmSetup>('/mail/setup'),
  mailboxes: () => get<{ data: CmMailbox[] }>('/mail/mailboxes'),
  connect: (b: { email: string; password: string; display_name?: string; smtp_host?: string; smtp_port?: number; imap_host?: string; imap_port?: number }) =>
    post<{ id: string; email: string }>('/mail/mailboxes/connect', b),
  create: (b: { local_part: string; domain: string; display_name?: string; quota_mb?: number }) =>
    post<{ id: string; email: string; warning?: string | null }>('/mail/mailboxes/create', b),
  updateMailbox: (id: string, b: { display_name?: string; sending_enabled?: boolean; sync_enabled?: boolean; password?: string }) =>
    patch<{ id: string }>(`/mail/mailboxes/${id}`, b),
  sync: (id: string) => post<{ queued: boolean }>(`/mail/mailboxes/${id}/sync`),
  disconnect: (id: string) => del<void>(`/mail/mailboxes/${id}`),

  items: (f: { mailbox?: string; direction?: string; bounces?: boolean; q?: string; limit?: number; offset?: number }) =>
    get<{ total: number; data: CmMailItem[] }>(`/mail/items${qs(f)}`),
  item: (id: string) => get<CmMailDetail>(`/mail/items/${id}`),
  send: (b: { mailbox_id: string; to: string[]; cc?: string[]; subject: string; html?: string; text?: string; reply_to_item_id?: string }) =>
    post<{ sent: boolean }>('/mail/send', b),

  lists: () => get<{ data: CmList[] }>('/audience/lists'),
  createList: (b: { name: string; description?: string; default_mailbox_id?: string; default_from_email?: string; double_opt_in?: boolean; redirect_url?: string }) =>
    post<{ id: string }>('/audience/lists', b),
  updateList: (id: string, b: { name?: string; description?: string; default_mailbox_id?: string; default_from_email?: string; double_opt_in?: boolean; redirect_url?: string }) =>
    patch<{ id: string }>(`/audience/lists/${id}`, b),
  deleteList: (id: string) => del<void>(`/audience/lists/${id}`),
  members: (id: string, f: { status?: string; q?: string; limit?: number; offset?: number }) =>
    get<{ total: number; data: CmMember[] }>(`/audience/lists/${id}/members${qs(f)}`),
  addMember: (id: string, b: { email: string; name?: string; consent_source: string; send_confirmation?: boolean }) =>
    post<{ email: string; status: string }>(`/audience/lists/${id}/members`, b),
  importMembers: (id: string, b: { csv: string; consent_source: string; send_confirmation?: boolean }) =>
    post<{ added: number; already_on_list: number; invalid: number; unsubscribed_or_bounced: number }>(`/audience/lists/${id}/import`, b),
  unsubscribeContact: (contactId: string) => post<{ status: string }>(`/audience/contacts/${contactId}/unsubscribe`),

  campaigns: () => get<{ data: CmCampaign[] }>('/campaigns'),
  campaign: (id: string) => get<CmCampaignDetail>(`/campaigns/${id}`),
  createCampaign: (b: CampaignFields) => post<{ id: string }>('/campaigns', b),
  updateCampaign: (id: string, b: CampaignFields) => patch<{ id: string }>(`/campaigns/${id}`, b),
  deleteCampaign: (id: string) => del<void>(`/campaigns/${id}`),
  testCampaign: (id: string, to: string) => post<{ sent: boolean }>(`/campaigns/${id}/test`, { to }),
  schedule: (id: string, send_at?: string) => post<{ status: string }>(`/campaigns/${id}/schedule`, { send_at }),
  pause: (id: string) => post<{ status: string }>(`/campaigns/${id}/pause`),
  resume: (id: string) => post<{ status: string }>(`/campaigns/${id}/resume`),
  cancel: (id: string) => post<{ status: string }>(`/campaigns/${id}/cancel`),
  spamCheck: (id: string) => post<CmSpamCheck>(`/campaigns/${id}/spam-check`),
  sends: (id: string, f: { status?: string; limit?: number; offset?: number }) =>
    get<{ total: number; data: CmSend[] }>(`/campaigns/${id}/sends${qs(f)}`),
}

const BADGES: Record<string, { text: string; cls: string }> = {
  draft: { text: 'Draft', cls: 'neutral' },
  scheduled: { text: 'Scheduled', cls: 'info' },
  sending: { text: 'Sending', cls: 'pending' },
  paused: { text: 'Paused', cls: 'neutral' },
  sent: { text: 'Sent', cls: 'ok' },
  cancelled: { text: 'Cancelled', cls: 'neutral' },
  queued: { text: 'Queued', cls: 'info' },
  failed: { text: 'Failed', cls: 'failed' },
  bounced: { text: 'Bounced', cls: 'failed' },
  skipped: { text: 'Skipped', cls: 'neutral' },
  subscribed: { text: 'Subscribed', cls: 'ok' },
  pending: { text: 'Awaiting confirmation', cls: 'pending' },
  unsubscribed: { text: 'Unsubscribed', cls: 'neutral' },
  active: { text: 'Active', cls: 'ok' },
}

export function cmBadge(status: string): { text: string; cls: string } {
  return BADGES[status] ?? { text: humanise(status), cls: 'info' }
}
